import React, { useState, useEffect } from 'react';
import {
  StarIcon,
  ShieldCheckIcon,
  ClockIcon,
  CheckBadgeIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { formatNumber } from '../../utils/formatters';
import { useP2PAds } from '../../hooks/useP2P';
import P2POrderModal from './P2POrderModal';

const P2PAdsList = ({ type, asset, fiat, paymentMethod, searchTerm, amountRange, priceRange }) => {
  const [selectedAd, setSelectedAd] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [type, asset, fiat, paymentMethod, searchTerm, amountRange.min, amountRange.max, priceRange.min, priceRange.max]);

  // Fetch ads from backend with filters
  const filters = {
    type,
    asset,
    fiat_currency: fiat,
    payment_method: paymentMethod !== 'all' ? paymentMethod : undefined,
    min_amount: amountRange.min || undefined,
    max_amount: amountRange.max || undefined,
    min_price: priceRange.min || undefined,
    max_price: priceRange.max || undefined,
    verified_only: false,
    merchant_only: false
  };

  const { ads, loading, error } = useP2PAds(filters);

  // Handle order creation
  const handleOrderClick = (ad) => {
    setSelectedAd(ad);
    setShowOrderModal(true);
  };

  // Filter ads based on search term
  const filteredAds = ads.filter(ad => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return ad.user_profile?.nickname?.toLowerCase().includes(search);
    }
    return true;
  });

  const totalPages = Math.ceil(filteredAds.length / 5);
  const currentPage = Math.min(page, Math.max(1, totalPages));
  const visibleAds = filteredAds.slice((currentPage - 1) * 5, currentPage * 5);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 sm:py-12">
        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-white text-sm sm:text-base">Loading advertisements...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8 sm:py-12">
        <p className="text-red-400 text-sm sm:text-base">Error loading advertisements: {error}</p>
      </div>
    );
  }

  // No ads found
  if (filteredAds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 sm:py-16">
        <EyeIcon className="h-12 w-12 sm:h-16 sm:w-16 text-white mb-3 sm:mb-4" />
        <h3 className="text-base sm:text-lg font-medium text-white mb-2">No ads found</h3>
        <p className="text-white text-center max-w-md text-sm sm:text-base px-4">
          Try adjusting your search criteria or filters to find available offers.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
        <h3 className="text-base sm:text-lg font-semibold text-white">
          {type === 'buy' ? 'Buy' : 'Sell'} {asset} with {fiat}
        </h3>
        <span className="text-xs sm:text-sm text-white">
          {filteredAds.length} ads available
        </span>
      </div>

      {totalPages > 1 && (
        <nav aria-label="Crypto ads pagination" className="flex flex-wrap items-center justify-center gap-4 mt-6">
          <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
          <span className="text-sm text-white">Page {currentPage} of {totalPages}</span>
          <button type="button" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
        </nav>
      )}

      <div className="space-y-4">
        {visibleAds.map(ad => (
          <div key={ad.ad_id} className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6 hover:border-gray-600 transition-colors">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-4 lg:space-y-0">
              {/* Merchant Info */}
              <div className="flex items-start space-x-3 sm:space-x-4 flex-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm sm:text-base">
                    {ad.user_profile?.nickname?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-white font-medium text-sm sm:text-base truncate">{ad.user_profile?.nickname || 'User'}</span>
                    {ad.user_profile?.is_verified && (
                      <CheckBadgeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 flex-shrink-0" />
                    )}
                    {ad.user_profile?.is_online && (
                      <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0"></div>
                    )}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-white mb-3">
                    <div className="flex items-center space-x-1">
                      <StarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-white fill-current" />
                      <span>{ad.user_profile?.rating?.toFixed(1) || '0.0'}</span>
                    </div>
                    <span>{ad.user_profile?.total_trades || 0} trades</span>
                    <span>{ad.user_profile?.completion_rate?.toFixed(1) || '0.0'}% completion</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {ad.payment_methods?.map(method => (
                      <span key={method.id} className="px-2 py-1 bg-gray-700 text-xs text-white rounded">
                        {method.name}
                      </span>
                    )) || (
                      <span className="px-2 py-1 bg-gray-700 text-xs text-white rounded">
                        No payment methods
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Price and Action */}
              <div className="text-left lg:text-right lg:ml-4">
                <div className="text-xl sm:text-2xl font-bold text-white mb-1">
                  {formatNumber(ad.price)} {ad.fiat_currency}
                </div>
                <div className="text-xs sm:text-sm text-white mb-2 sm:mb-3">
                  Available: {formatNumber(ad.available_amount)} {ad.asset}
                </div>
                <div className="text-xs text-white mb-3 sm:mb-4">
                  Limits: {formatNumber(ad.min_order_amount)} - {formatNumber(ad.max_order_amount)} {ad.fiat_currency}
                </div>
                
                <button 
                  onClick={() => handleOrderClick(ad)}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium text-sm sm:text-base"
                >
                  {type === 'buy' ? 'Buy' : 'Sell'} {ad.asset}
                </button>
              </div>
            </div>
            
            {/* Terms */}
            {ad.terms_and_conditions && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-xs sm:text-sm text-white">{ad.terms_and_conditions}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Order Modal */}
      <P2POrderModal
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedAd(null);
        }}
        ad={selectedAd}
        type={type}
      />
    </div>
  );
};

export default P2PAdsList;